import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const ROLE_LABELS = {
  business_admin: 'Business Admin',
  team_manager:   'Team Manager',
  agent:          'Agent / Developer',
  api_partner:    'API Partner',
};
const ROLES = Object.keys(ROLE_LABELS);

export default function InvitationManager() {
  const [invitations, setInvitations] = useState([]);
  const [members, setMembers]         = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode]   = useState('create'); // 'create' = direct add, 'invite' = email invite
  const [form, setForm]   = useState({ name: '', email: '', role: 'agent', message: '' });
  const [tab, setTab]     = useState('members');
  const [tempCred, setTempCred] = useState(null); // { email, temp_password } shown once

  const load = async () => {
    setLoading(true);
    try {
      const [i, m] = await Promise.all([
        api.get('/tenant/invitations'),
        api.get('/tenant/members'),
      ]);
      setInvitations(i.data);
      setMembers(m.data.members || []);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.email.trim()) { toast.error('Email required'); return; }
    if (mode === 'create' && !form.name.trim()) { toast.error('Name required for direct add'); return; }
    try {
      if (mode === 'create') {
        const res = await api.post('/tenant/members', { name: form.name, email: form.email, role: form.role });
        if (res.data.temp_password) {
          setTempCred({ email: res.data.email, temp_password: res.data.temp_password });
        } else {
          toast.success('Existing user added to your company');
        }
      } else {
        await api.post('/tenant/invitations', { email: form.email, role: form.role, message: form.message });
        toast.success(`Invitation sent to ${form.email}`);
      }
      setShowForm(false);
      setForm({ name: '', email: '', role: 'agent', message: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const revoke = async (id) => {
    try { await api.delete(`/tenant/invitations/${id}`); toast.success('Invitation revoked'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member from the workspace?')) return;
    try { await api.delete(`/tenant/members/${userId}`); toast.success('Member removed'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const statusColor = (s) => ({ pending: '#fab387', accepted: '#a6e3a1', expired: '#f38ba8', revoked: 'var(--text-3)' }[s] || 'var(--text-2)');
  const roleBadge = (r) => ({ business_admin: '#5865f2', team_manager: '#fab387', agent: '#a6e3a1', api_partner: '#89b4fa' }[r] || '#cdd6f4');
  const S = { card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 } };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          {['members','invitations'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border)', background: tab === t ? 'var(--accent)' : 'transparent', color: tab === t ? '#fff' : 'var(--text-2)', cursor: 'pointer', fontSize: 13, textTransform: 'capitalize' }}>
              {t}
            </button>
          ))}
        </div>
        <button onClick={() => { setShowForm(!showForm); setTempCred(null); }} className="btn btn-primary">＋ Add Member</button>
      </div>

      {/* One-time temp-credential reveal */}
      {tempCred && (
        <div style={{ ...S.card, borderColor: '#a6e3a1', marginBottom: 16 }}>
          <div style={{ color: '#a6e3a1', fontWeight: 600, marginBottom: 8 }}>✓ User created — share these credentials now (shown once)</div>
          <div style={{ fontSize: 13, marginBottom: 4 }}>Email: <code style={{ background: 'var(--bg)', padding: '2px 8px', borderRadius: 6 }}>{tempCred.email}</code></div>
          <div style={{ fontSize: 13 }}>Temp password: <code style={{ background: 'var(--bg)', padding: '2px 8px', borderRadius: 6 }}>{tempCred.temp_password}</code></div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>The user will be required to set a new password on first login.</div>
          <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
            <button onClick={() => { navigator.clipboard.writeText(`Email: ${tempCred.email}\nTemp password: ${tempCred.temp_password}`); toast.success('Copied!'); }} className="btn btn-ghost" style={{ fontSize: 12 }}>Copy</button>
            <button onClick={() => setTempCred(null)} className="btn btn-ghost" style={{ fontSize: 12 }}>Done ✓</button>
          </div>
        </div>
      )}

      {showForm && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'var(--bg)', borderRadius: 8, padding: 4, width: 'fit-content' }}>
            {[['create','Create directly'],['invite','Invite by email']].map(([m, label]) => (
              <button key={m} onClick={() => setMode(m)}
                style={{ padding: '5px 12px', borderRadius: 6, border: 'none', background: mode === m ? 'var(--accent)' : 'transparent', color: mode === m ? '#fff' : 'var(--text-2)', cursor: 'pointer', fontSize: 12 }}>
                {label}
              </button>
            ))}
          </div>

          {mode === 'create' && (
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Full name *" className="input" style={{ width: '100%', marginBottom: 10 }} />
          )}
          <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="colleague@company.com *" className="input" style={{ width: '100%', marginBottom: 10 }} />
          <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="input" style={{ width: '100%', marginBottom: 10 }}>
            {ROLES.map(r => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </select>
          {mode === 'invite' && (
            <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Personal message (optional)" className="input" style={{ width: '100%', marginBottom: 12, height: 72, resize: 'vertical' }} />
          )}
          {mode === 'create' && (
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 12 }}>
              A temporary password will be generated and shown to you once. If the email already has an account, that user is simply added to your company.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={submit} className="btn btn-primary">{mode === 'create' ? 'Create user' : 'Send invite'}</button>
            <button onClick={() => setShowForm(false)} className="btn btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>Loading…</div> : (
        <>
          {tab === 'members' && (
            members.length === 0
              ? <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-3)', padding: 40 }}>No members yet. Add your team!</div>
              : members.map(m => (
                <div key={m.id} style={{ ...S.card, marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 9, background: roleBadge(m.role) + '33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: roleBadge(m.role) }}>{m.name?.[0]?.toUpperCase()}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{m.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{m.email}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: roleBadge(m.role) + '22', color: roleBadge(m.role) }}>{ROLE_LABELS[m.role] || m.role}</span>
                    <button onClick={() => removeMember(m.id)} className="btn btn-ghost" style={{ fontSize: 11, color: '#f38ba8' }}>Remove</button>
                  </div>
                </div>
              ))
          )}
          {tab === 'invitations' && (
            invitations.length === 0
              ? <div style={{ ...S.card, textAlign: 'center', color: 'var(--text-3)', padding: 40 }}>No pending invitations.</div>
              : invitations.map(i => (
                <div key={i.id} style={{ ...S.card, marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{i.email}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Role: {ROLE_LABELS[i.role]} · Invited by {i.invited_by_name} · Expires {new Date(i.expires_at).toLocaleDateString()}</div>
                    </div>
                    <span style={{ color: statusColor(i.status), fontSize: 12, fontWeight: 600 }}>{i.status}</span>
                    {i.status === 'pending' && <button onClick={() => revoke(i.id)} className="btn btn-ghost" style={{ fontSize: 11, color: '#f38ba8' }}>Revoke</button>}
                  </div>
                </div>
              ))
          )}
        </>
      )}
    </div>
  );
}
